import { useEffect, useMemo, useRef, useState } from "react";
import { GoIcon, StarIcon } from "../../icons";
import { renderPrice } from "../../utils/render-price";
import { getMonthlyLoanPayment } from "../../utils/get-monthly-loan-payment";
import { useAppContext } from "../../context";
import { calculateLoan } from "../../utils/calculate-loan";
import productData from '../../data/product-data.json';

export const Quote = ({
  option,
  shouldRenderSelectButton = true,
  onChange,
}) => {
  const {
    setSelectedOption,
    setCurrentStep,
    isLoading,
    setIsLoading,
    selectedOption,
  } = useAppContext();

  const [isHighlightOpen, setIsHighlightOpen] = useState(false);
  const starCount = { 1: 3, 2: 2, 3: 1 }[option.rank];
  const starText = { 1: "Best", 2: "Better", 3: "Good" }[option.rank];

  const cardTagText = {
    1: "Best Performance Bundle",
    2: "Our Recommended Bundle",
    3: "Best Value Bundle",
  }[option.rank];

  const total = useRef(option.total);

  const addonsSum = option.addons
    .filter((addon) => addon.isSelected)
    .reduce((acc, addon) => acc + addon.price, 0);

  const warranty = option.isWarrantySelected ? option.totalWarranty : 0;
  const totalWithoutWarrantyAndAddons = option.total - warranty - addonsSum;

  const productBreakdown = useMemo(() => {
    const selectedAddons =
      selectedOption?.addons.filter((addon) => addon.isSelected) || [];

    const { equipments, restItems } = option.items.reduce(
      (acc, item) => {
        if (item.readonly) {
          return acc;
        }

        if (
          item.title.includes("HVAC") ||
          item.title.includes("Heat Pump Stand") ||
          item.title.includes("Heat Pump Bracket")
        ) {
          acc.equipments.push(item);
        } else {
          acc.restItems.push(item);
        }
        return acc;
      },
      { equipments: [], restItems: [] }
    );

    const equipment =
      equipments.length > 0
        ? equipments.reduce(
            (acc, item) => {
              return {
                ...acc,
                title: "Additional Equipment",
                price: acc.price + item.price,
              };
            },
            { price: 0 }
          )
        : null;

    return [...restItems, ...(equipment ? [equipment] : []), ...selectedAddons];
  }, [option.items, selectedOption]);

  useEffect(() => {
    // add loan if total changes
    if (total.current !== option.total) {
      total.current = option.total;

      const addLoan = async () => {
        setIsLoading(true);
        const loans = await calculateLoan(option.total);
        onChange({ ...option, loans });
        setIsLoading(false);
      };

      addLoan();
    }
  }, [option.total]);

  // Get province from option or context
  const province = option.province || 'ON'; // Default to ON if not present
  const provTaxRate = productData.prov_tax_rate[province] || 1.13;

  // Subtotal is option.total
  const subtotal = option.total;
  // Tax by Province
  const taxUnrounded = subtotal * (provTaxRate - 1);
  // Round subtotal and tax to 2 decimals for display
  const subtotalRounded = Math.round(subtotal * 100) / 100;
  const tax = Math.round(taxUnrounded * 100) / 100;
  // Total should be the exact sum of displayed Subtotal and Estimated Taxes
  const totalWithTax = Math.round((subtotalRounded + tax) * 100) / 100;

  return (
    <div
      className={`tw-rounded tw-bg-white ${
        option.rank === 1 ? "tw-border tw-border-blue" : ""
      }`}
    >
      <div
        className={`tw-h-[32px] tw-flex tw-items-center tw-justify-center ${
          option.rank === 1 ? "tw-bg-blue" : "tw-bg-[#ECF0F4]"
        }`}
      >
        <span
          className={`tw-text-sm tw-font-semibold ${
            option.rank === 1 ? "tw-text-white" : "tw-text-blue"
          }`}
        >
          {cardTagText}
        </span>
      </div>
      <div className="md:tw-px-12 tw-px-3 tw-pt-5 tw-pb-7 md:tw-pb-12">
        <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-start md:tw-items-center">
          <h2 className="tw-text-2xl tw-text-blue tw-font-bold">
            {option.title}
          </h2>
          <span
            className={`tw-rounded-full tw-px-4 tw-py-1 tw-text-white tw-font-semibold tw-uppercase tw-inline-flex tw-items-center tw-gap-x-1 tw-mt-5 md:tw-mt-0 md:tw-ml-8 ${
              option.rank === 1 ? "tw-bg-orange" : "tw-bg-blue"
            }`}
          >
            <span className="tw-mr-2">{starText}</span>
            {Array.from({ length: starCount }).map((_, i) => (
              <StarIcon key={i} />
            ))}
          </span>
        </div>
        <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-mt-6">
          <div className="tw-col-span-12 md:tw-col-span-3">
            <img
              src={option.image}
              alt=""
              className="tw-w-full tw-h-full tw-object-contain"
            />
          </div>
          <div className="tw-flex tw-flex-col tw-justify-between tw-text-blue tw-col-span-12 md:tw-col-span-6">
            <div className="tw-border tw-border-blue tw-rounded-lg tw-py-5 tw-px-6">
              <div className="tw-flex tw-flex-col">
                <button
                  className="tw-font-bold tw-mt-3 tw-underline tw-underline-offset-4 tw-flex"
                  onClick={() => setIsHighlightOpen((prev) => !prev)}
                >
                  Product Highlights
                  <span className={`${isHighlightOpen ? "tw-rotate-90" : ""}`}>
                    <GoIcon />
                  </span>
                </button>
                {isHighlightOpen && (
                  <p className="tw-mt-2">{option.highlights}</p>
                )}
                <p className="tw-font-bold tw-mb-2 tw-mt-7">
                  Product breakdown
                </p>
                <div>
                  {productBreakdown.map((item, i) => {
                    return (
                      <>
                        <div className="tw-flex tw-justify-between" key={i}>
                          <p>{item.title}</p>
                          <p className="tw-font-medium tw-shrink-0">
                            {renderPrice(item.price)}
                          </p>
                        </div>
                      </>
                    );
                  })}
                </div>
              </div>
              <div className="tw-flex tw-mt-4 tw-justify-between">
                <div className="tw-flex tw-flex-col tw-w-full">
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-text-sm tw-font-normal tw-leading-5 tw-tracking-[-0.01em] tw-text-left tw-uppercase tw-text-gray-500">
                      Subtotal
                    </span>
                    <span className="tw-text-sm tw-font-normal tw-leading-5 tw-tracking-[-0.01em] tw-text-left tw-text-gray-500">
                      {renderPrice(subtotal)}
                    </span>
                  </div>
                  <div className="tw-flex tw-justify-between tw-mt-1">
                    <span className="tw-text-sm tw-font-normal tw-leading-5 tw-tracking-[-0.01em] tw-text-left tw-uppercase tw-text-gray-500">
                      Estimated Taxes
                    </span>
                    <span className="tw-text-sm tw-font-normal tw-leading-5 tw-tracking-[-0.01em] tw-text-left tw-text-gray-500">
                      {renderPrice(tax)}
                    </span>
                  </div>
                  <div className="tw-flex tw-justify-between tw-mt-2">
                    <span className="tw-text-lg tw-font-bold tw-leading-6 tw-tracking-[-0.01em] tw-text-left tw-uppercase tw-text-blue-700">
                      Total
                    </span>
                    <span className="tw-text-lg tw-font-bold tw-leading-6 tw-tracking-[-0.01em] tw-text-left tw-text-blue-700">
                      {renderPrice(totalWithTax)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="tw-border tw-border-blue tw-rounded-lg tw-px-6 tw-py-2 tw-mt-3 tw-shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] md:tw-shadow-none tw-bg-[#E5FAF7]">
              <div className="tw-flex tw-justify-between">
                <p>Available incentives up to</p>
                <p className="tw-font-bold">
                  {renderPrice(option.grantAllowance)}
                </p>
              </div>
              {option.isWarrantySelected && (
                <div className="tw-flex tw-justify-between">
                  <p>Extended Warranty</p>
                  <p className="tw-font-bold">
                    {renderPrice(option.totalWarranty)}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="tw-bg-gray-light tw-flex tw-flex-col tw-justify-between tw-items-center tw-pt-6 tw-pb-6 tw-rounded-lg tw-col-span-12 md:tw-col-span-3">
            <div>
              <p className="tw-text-base tw-text-blue tw-font-normal tw-leading-5 tw-text-center">
                Total price:
              </p>
              <h2 className="tw-text-2xl tw-font-medium tw-text-blue tw-text-center">
                {renderPrice(
                  Math.max(0, totalWithTax - option.grantAllowance)
                )}
              </h2>
              <h3 className="tw-text-lg tw-font-medium tw-text-gray tw-text-center tw-line-through">
                {renderPrice(totalWithTax)}
              </h3>
            </div>
            <div className="tw-pt-14 tw-pb-20">
              {option.loans && (
                <>
                  <p className="tw-text-sm tw-text-blue tw-text-center">
                    Or from as little as
                  </p>
                  {isLoading ? (
                    <div className="skeleton-loading tw-h-10 tw-w-full tw-rounded-lg"></div>
                  ) : (
                    <h1 className="tw-text-3xl tw-font-bold tw-text-orange tw-text-center">
                      {renderPrice(getMonthlyLoanPayment(option.loans))}
                      /mth*
                    </h1>
                  )}
                </>
              )}
            </div>

{/*            
            <div className="tw-pb-5">
              <label className="tw-text-sm tw-cursor-pointer tw-font-medium tw-flex tw-items-center tw-gap-x-1 tw-text-center">
                <input
                  type="checkbox"
                  className="tw-accent-blue tw-w-4 tw-h-4"
                  checked={option.isWarrantySelected}
                  disabled={isLoading}
                  onChange={(e) =>
                    onChange({
                      ...option,
                      total: e.target.checked
                        ? option.total + option.totalWarranty
                        : option.total - option.totalWarranty,
                      isWarrantySelected: e.target.checked,
                    })
                  }
                />
                Add Extended Warranty
                <span className="tw-w-4 tw-h-4 tw-rounded-full tw-bg-gray tw-text-white tw-text-[10px] tw-font-semibold tw-text-center tw-flex tw-items-center tw-justify-center">
                  ?
                </span>
              </label>
            </div>
 */}

            
            {shouldRenderSelectButton && (
              <div className="tw-px-9 tw-w-full">
                <button
                  className="tw-w-full tw-py-6 tw-px-8 tw-bg-orange tw-font-semibold tw-rounded-full tw-text-white"
                  onClick={() => {
                    setSelectedOption(option);
                    setCurrentStep("selected");
                  }}
                >
                  Select Option
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
